import React, { useEffect, useRef, useState } from 'react'
import { useParticipantList } from './hooks'
import { CButton } from '@coreui/react-pro'
import { Badge, Flex, InputRef, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import CIcon from '@coreui/icons-react'
import { cilDelete, cilNoteAdd } from '@coreui/icons'
import { Participant, ParticipantClient } from './types'
import { FilterValue } from 'antd/es/table/interface'
import { DisqualifyParticipantModal } from '../DisqualifyParticipantModal'
import { AwardedCashDepositModal } from '../AwardedCashDepositModal/component'
import { path } from '../../../../libs'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../../libs/formatCountries'

export const ParticipantsTable: React.FC = () => {
  const [openModalProps, setOpenModalProps] = useState({
    isOpen: false,
    id: '',
    participantId: '',
    mode: '',
  })

  const {
    participantList,
    totalCountOfParticipantList,
    loading,
    onChangeParticipantList,
  } = useParticipantList()

  const countriesList = useUnit($countriesList)

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('accountId')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeParticipantList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const columns: ColumnsType<Participant> = [
    {
      title: 'Place',
      dataIndex: 'place',
      sorter: (a, b) => a.place - b.place,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Name',
      dataIndex: 'client',
      key: 'client.hiddenFullName',
      filters: transformCountriesToOptions(countriesList),
      filterSearch: true,
      render: (item: ParticipantClient) => (
        <>
          <div>{item?.hiddenFullName}</div>
        </>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client.email',
      ...getColumnSearchProps<Participant>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: ParticipantClient) => (
        <div>
          {item?.isTest ? (
            <Badge count={'Test'} offset={[-5, -10]}>
              <a
                href={path.clients.details(String(item.clientId))}
                target="_blank"
              >
                {item?.email}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.clientId))}
              target="_blank"
            >
              {item?.email}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'client',
      key: 'client.country',
      filters: transformCountriesToOptions(countriesList),
      filterSearch: true,
      render: (item: ParticipantClient) => (
        <>
          <div>{item?.country}</div>
        </>
      ),
    },
    {
      title: 'MT Account login',
      dataIndex: 'mtLogin',
      key: 'mtLogin',
      ...getColumnSearchProps<Participant>(
        'mtLogin',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Team',
      dataIndex: 'teamNumber',
      key: 'teamNumber',
      filterMultiple: false,
      filters: [
        { value: 'first', text: 'First' },
        { value: 'second', text: 'Second' },
      ],
    },
    {
      title: 'Equity',
      dataIndex: 'equity',
      sorter: true,
    },
    {
      title: 'Disqualified at',
      dataIndex: 'disqualifiedAt',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <Flex gap={8}>
            <CButton
              color="success"
              className="me-2"
              title="View"
              onClick={() =>
                setOpenModalProps((prev) => ({
                  ...prev,
                  id: item.userId,
                  participantId: item.id,
                  isOpen: true,
                  mode: 'deposit',
                }))
              }
            >
              <CIcon icon={cilNoteAdd} />
            </CButton>
            <CButton
              color="danger"
              className="me-2"
              title="View"
              onClick={() =>
                setOpenModalProps((prev) => ({
                  ...prev,
                  id: item.id,
                  isOpen: true,
                  mode: 'disqualify',
                }))
              }
            >
              <CIcon icon={cilDelete} />
            </CButton>
          </Flex>
        )
      },
    },
  ]

  return (
    <>
      <Table
        dataSource={participantList}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="participantsTableID"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfParticipantList,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      />
      <AwardedCashDepositModal
        userId={openModalProps.id}
        participantId={openModalProps.participantId}
        isVisible={openModalProps.isOpen && openModalProps.mode === 'deposit'}
        onClose={() =>
          setOpenModalProps((prev) => ({ ...prev, isOpen: false, mode: '' }))
        }
      />
      <DisqualifyParticipantModal
        id={openModalProps.id}
        isVisible={
          openModalProps.isOpen && openModalProps.mode === 'disqualify'
        }
        onClose={() =>
          setOpenModalProps((prev) => ({ ...prev, isOpen: false, mode: '' }))
        }
      />
    </>
  )
}
